import React from "react";
import { Form, Pagination } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash, FaRegCopy } from "react-icons/fa";
import { FaRegFileCode } from "react-icons/fa6";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import DateRangePicker from "../../../pages/sourceBusiness/DateRangePicker";
import { clearDateRange } from "../../../redux/actions/dateRangeActions";
import "./newTable.css";

const CommonDataTable = ({
  data: filteredData,
  expiredData = [],
  onView,
  onEdit,
  onDelete,
  currentPage,
  totalPages,
  onPageChange,
  showSearch = false,
  setCompaignForSearch,
  compaignSearch,
  columns = [],
  onToggleStatus,
  onToggleVerified,
  searchText = "Search...",
  dateChange = false,
  handleCopy = null,
  copyPrevData = null,
  showExpiringOffer = false,
  enableHorizontalScroll = false,
}) => {
  const hasActions = Boolean(onView || onEdit || onDelete || handleCopy || copyPrevData);
  const tableColumns = showExpiringOffer
    ? [
      { key: "offering_id", label: "Offering ID" },
      { key: "brand_name", label: "Brand Name" },
      { key: "details", label: "Details" },
      { key: "validity", label: "Validity" },
      { key: "status", label: "Status" },
    ]
    : columns;

  const scrollMinWidth = Math.max(
    (tableColumns?.length || 0) * 170 + (hasActions ? 180 : 0),
    980
  );
  const { endDate } = useSelector((state) => state?.dateRangeReducer, shallowEqual);
  const dispatch = useDispatch();

  const handleToggle = (item, key) => {
    const newValue = !item[key];
    if (key === "status" && onToggleStatus) {
      if (item?.brand_id && !item?.batch_id) {
        onToggleStatus(item?.brand_id, newValue);
      } else if (item?.batch_id) {
        onToggleStatus(item?.batch_id, newValue);
      } else {
        onToggleStatus(item?.offering_id, newValue);
      }
    } else if (key === "is_verified" && onToggleVerified) {
      if (item?.brand_id && !item?.batch_id) {
        onToggleVerified(item?.brand_id, newValue);
      } else if (item?.batch_id) {
        onToggleVerified(item?.batch_id, newValue);
      } else {
        onToggleVerified(item?.offering_id, newValue);
      }
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
  };

  const isToggleEnabled = (key) =>
    (key === "status" && Boolean(onToggleStatus)) ||
    (key === "is_verified" && Boolean(onToggleVerified));

  const renderCellValue = (item, col) => {
    const value = item?.[col.key];

    if (isToggleEnabled(col.key)) {
      return (
        <Form.Check
          type="switch"
          id={`${col.key}-${item?.id || item?.offering_id || item?.batch_id || item?.collection_id}`}
          checked={Boolean(value)}
          onChange={() => handleToggle(item, col.key)}
          className="common-data-table-switch"
        />
      );
    }

    if (col.key === "status") {
      return (
        <span className={`common-data-table-pill ${value ? "is-active" : "is-inactive"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      );
    }

    if (col.key === "is_verified") {
      return (
        <span className={`common-data-table-pill ${value ? "is-verified" : "is-unverified"}`}>
          {value ? "Verified" : "Not Verified"}
        </span>
      );
    }

    if (col.key === "validity") {
      return <span className="common-data-table-date">{formatDate(value)}</span>;
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return String(value);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const paginationItems = [];
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 3) endPage = Math.min(5, totalPages - 1);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 2);

    paginationItems.push(
      <Pagination.Item key={1} active={currentPage === 1} onClick={() => onPageChange(1)}>1</Pagination.Item>
    );

    if (startPage > 2) paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    for (let i = startPage; i <= endPage; i++) {
      paginationItems.push(
        <Pagination.Item key={i} active={currentPage === i} onClick={() => onPageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages - 1) paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    if (totalPages > 1) {
      paginationItems.push(
        <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => onPageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="common-data-table-pagination">
        <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
        {paginationItems}
        <Pagination.Next onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  const expiredIds = new Set(expiredData?.map((o) => o.offering_id));

  return (
    <div className="common-data-table">
      <div className="common-data-table-toolbar">
        <div className="common-data-table-toolbar-left">
          {showExpiringOffer && (
            <span className="common-data-table-chip">Expiring Offers</span>
          )}
        </div>
        <div className="common-data-table-toolbar-right">
        {dateChange && <DateRangePicker />}
        {dateChange && (
          <button
            onClick={() => dispatch(clearDateRange())}
            className="common-data-table-clear-btn"
            disabled={!endDate}
          >
            Clear
          </button>
        )}
        {showSearch && (
          <Form.Control
            type="text"
            placeholder={searchText}
            value={compaignSearch}
            onChange={(e) => setCompaignForSearch(e.target.value)}
            className="common-data-table-search"
          />
        )}
        </div>
      </div>

      <div
        className="common-data-table-scroll"
        style={{
          maxHeight: "560px",
          maxWidth: enableHorizontalScroll ? "100%" : "1400px",
          width: "100%",
        }}
      >
        <table
          className={`common-data-table-grid ${showExpiringOffer ? "is-expiring" : ""}`}
          style={
            enableHorizontalScroll
              ? { width: "max-content", minWidth: `${scrollMinWidth}px` }
              : undefined
          }
        >
          <thead>
            <tr>
              {tableColumns?.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              {hasActions && <th className="common-data-table-actions-col">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredData?.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColumns.length + (hasActions ? 1 : 0)}
                  className="common-data-table-empty"
                >
                  {showExpiringOffer ? "No expiring offers found." : "No results found."}
                </td>
              </tr>
            ) : (
              filteredData?.map((item, index) => {
                const rowKey =
                  item?.id ||
                  item?.offering_id ||
                  item?.batch_id ||
                  item?.collection_id ||
                  `row-${index}`;
                const isExpired = expiredIds.has(item?.offering_id);

                return (
                  <tr key={rowKey} className={isExpired ? "is-expired" : ""}>
                    {tableColumns.map((col) => (
                      <td
                        key={`${rowKey}-${col.key}`}
                        title={String(item?.[col.key] ?? "-")}
                        className={`common-data-table-cell ${col.key === "offering_id" && isExpired ? "has-expired-tag" : ""
                          }`}
                      >
                        <span className="common-data-table-cell-text">
                          {renderCellValue(item, col)}
                        </span>
                        {col.key === "offering_id" && isExpired && (
                          <span className="common-data-table-expired-tag">Expired</span>
                        )}
                      </td>
                    ))}

                    {hasActions && (
                      <td className="common-data-table-actions">
                        {onView && (
                          <button
                            onClick={() => onView(item)}
                            className="table-action-btn is-view"
                            aria-label="View row"
                          >
                            <FaEye />
                          </button>
                        )}
                        {handleCopy && (
                          <button
                            onClick={() => handleCopy(item)}
                            className="table-action-btn is-copy"
                            aria-label="Copy code"
                          >
                            <FaRegFileCode />
                          </button>
                        )}
                        {copyPrevData && (
                          <button
                            onClick={() => copyPrevData(item)}
                            className="table-action-btn is-copy"
                            aria-label="Copy previous data"
                          >
                            <FaRegCopy />
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => onEdit(item)}
                            className="table-action-btn is-edit"
                            aria-label="Edit row"
                          >
                            <FaEdit />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(item)}
                            className="table-action-btn is-delete"
                            aria-label="Delete row"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && renderPagination()}
    </div>
  );
};

export default CommonDataTable;
